import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { observer } from 'mobx-react-lite';
import { Button } from '@linktivity/link-ui';

import { useStore } from '@link/shinkansen/stores';

import styles from './confirm.module.css';

const ConfirmView = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { shinkansen } = useStore();
  const { bookingInfo, selection } = shinkansen;
  const { diagram, fromStation, toStation, seatClass, seatPreference } =
    selection;
  const [submitting, setSubmitting] = useState(false);

  const handleConfirm = async () => {
    if (submitting) {
      return;
    }
    setSubmitting(true);
    try {
      const { orderNo } = await shinkansen.submitBooking();
      // Only advance once we have a real order number; otherwise Submitted
      // would refresh with a missing/wrong booking id and render empty.
      if (orderNo) {
        navigate('../submitted', {
          replace: true,
          state: { submittedOrder: true, tUrl: shinkansen.tUrl }
        });
      }
    } catch {
      // Order creation failed; the error toast is surfaced by the endpoint
      // middleware, so stay on this page for the user to retry.
    } finally {
      setSubmitting(false);
    }
  };

  const fromStationName = fromStation?.name;
  const toStationName = toStation?.name;
  const units =
    bookingInfo?.units?.map(unit => `${unit.name} ×${unit.quantity ?? 0}`) ??
    [];
  const trainType = diagram?.trainMode
    ? `${diagram.trainMode}${
        diagram.trainName ? `（${diagram.trainName}）` : ''
      }`
    : '';
  const seatClassName =
    seatClass?.seatClassName || bookingInfo?.seatClassName || '';

  // Deep-link / refresh onto /confirm without a picked train — the in-flight
  // selection is gone, so show an empty state instead of a blank summary.
  if (!diagram) {
    return <p className={styles.empty}>{t('common.noBooking')}</p>;
  }

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <h1 className={styles.title}>{t('views.confirm.trainConfirm')}</h1>

        <section className={styles.card}>
          <div className={styles.trainRow}>
            <div className={styles.trainTime}>
              <span className={styles.time}>{diagram?.departTime}</span>
              <span className={styles.station}>{fromStationName}</span>
            </div>

            <div className={styles.trainMiddle}>
              <span className={styles.duration}>{diagram?.timeDuration}</span>
              <div className={styles.line} />
              <span className={styles.trainName}>{diagram?.trainName}</span>
            </div>

            <div className={styles.trainTime}>
              <span className={styles.time}>{diagram?.arriveTime}</span>
              <span className={styles.station}>{toStationName}</span>
            </div>
          </div>

          <div className={styles.divider} />

          <div className={styles.details}>
            <h2 className={styles.ticketName}>
              {t('views.confirm.ticketName')}
            </h2>

            {(fromStationName || toStationName) && (
              <div className={styles.detailRow}>
                <span className={styles.label}>{t('views.confirm.route')}</span>
                <span className={styles.value}>
                  {fromStationName} → {toStationName}
                </span>
              </div>
            )}

            {bookingInfo?.departDate && (
              <div className={styles.detailRow}>
                <span className={styles.label}>
                  {t('views.confirm.travelDate')}
                </span>
                <span className={styles.value}>{bookingInfo.departDate}</span>
              </div>
            )}

            {units.length > 0 && (
              <div className={styles.detailRow}>
                <span className={styles.label}>
                  {t('views.confirm.ticketCount')}
                </span>
                <span className={styles.value}>{units.join(' ')}</span>
              </div>
            )}

            {trainType && (
              <div className={styles.detailRow}>
                <span className={styles.label}>
                  {t('views.confirm.trainType')}
                </span>
                <span className={styles.value}>{trainType}</span>
              </div>
            )}

            {seatClassName && (
              <div className={styles.detailRow}>
                <span className={styles.label}>
                  {t('views.confirm.seatClass')}
                </span>
                <span className={styles.value}>{seatClassName}</span>
              </div>
            )}

            {seatPreference?.name && (
              <div className={styles.detailRow}>
                <span className={styles.label}>
                  {t('views.confirm.preference')}
                </span>
                <span className={styles.value}>{seatPreference.name}</span>
              </div>
            )}
          </div>
        </section>
      </div>

      <div className={styles.footer}>
        <Button
          variant="filled"
          className={styles.confirmButton}
          loading={submitting}
          disabled={!diagram}
          onClick={handleConfirm}
        >
          {t('views.confirm.confirmSelect')}
        </Button>
      </div>
    </div>
  );
};

const Confirm = observer(ConfirmView);

export default Confirm;
